import { withArticle } from '@fruitful/fruitils';
import {
    buildSimulation,
    HIGH_INTEREST_THRESHOLD,
} from '@fruitful/money-map-simulator';
import { CMMBranch } from '@fruitful/types';
import React, {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { type LayoutChangeEvent, StyleSheet } from 'react-native';
import Animated, {
    Easing,
    interpolate,
    type SharedValue,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';
import {
    BadgeLabel,
    BodyLargeRegular,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonVariant,
    FontFamilyName,
    FruitfulCustomIcon,
    H6Regular,
    Image,
    isNonMobileWeb,
    isWeb,
    LabelRegular,
    Shadows,
    Spinner,
    SpinnerSize,
    XStack,
    YStack,
} from 'smoothie';

import GuidesImage from '../../assets/images/guides.png';
import { ConversationalMMAnalyticsEvent } from '../analytics/events';
import { useConversationalMMContext } from '../context/ConversationalMMContextBase';
import {
    buildStaticRuleSetDisplays,
    computeRuleSetDisplays,
} from '../utils/simulationDisplayUtils';
import {
    Header,
    LinearGradientBackgroundCard,
    TrustpilotRatingBadge,
} from './shared';

const EXPAND_DURATION_MS = 400;

const AnimatedGoalCard = ({
    index,
    expandProgress,
    expandedCards,
    children,
}: {
    index: number;
    expandProgress: SharedValue<number>;
    expandedCards: boolean;
    children: React.ReactNode;
}) => {
    const collapsedMarginTop = index === 0 ? 0 : index <= 2 ? -80 : -100;
    const collapsedOpacity = index < 3 ? 1 : 0;

    const animatedStyle = useAnimatedStyle(() => ({
        marginTop: interpolate(
            expandProgress.value,
            [0, 1],
            [collapsedMarginTop, 0],
        ),
        opacity: interpolate(
            expandProgress.value,
            [0, 1],
            [collapsedOpacity, 1],
        ),
        zIndex: 100 - index,
        alignSelf: 'center' as const,
    }));

    return (
        <Animated.View
            style={[
                {
                    width: expandedCards
                        ? '100%'
                        : `${100 - Math.min(index, 2) * 2}%`,
                },
                isWeb &&
                    ({
                        transition: `width ${EXPAND_DURATION_MS}ms ease`,
                    } as unknown as object),
                animatedStyle,
            ]}
        >
            {children}
        </Animated.View>
    );
};

const TrialSignupCard = ({
    containerWidth,
    isSubmitting,
    onSubmit,
}: {
    containerWidth: number;
    isSubmitting: boolean;
    onSubmit: () => void;
}) => {
    return (
        <LinearGradientBackgroundCard>
            <YStack
                gap={32}
                width="100%"
                justifyContent="center"
                alignItems="center"
            >
                <H6Regular textAlign="center">
                    This is your starting point, built from everything
                    you&apos;ve shared so far.
                </H6Regular>
                <BodyMediumRegular textAlign="center">
                    In your first session, your dedicated Fruitful Guide, a CFP®
                    Professional, will fine-tune everything with you.
                </BodyMediumRegular>
                <Image
                    source={GuidesImage}
                    style={{
                        width: containerWidth + 32,
                        height: containerWidth / 2,
                        marginBottom: -48, // image has extra padding on the bottom for a drop shadow, account for that to keep flex gap visually consistent
                    }}
                />
                <BodyMediumRegular textAlign="center">
                    You&apos;re building something real here, and you&apos;re
                    not doing it alone.
                </BodyMediumRegular>
                <Button
                    variant={ButtonVariant.PRIMARY}
                    text="Continue"
                    icon={
                        isSubmitting ? (
                            <Spinner size={SpinnerSize.SMALL} width="auto" />
                        ) : undefined
                    }
                    disabled={isSubmitting}
                    onPress={onSubmit}
                />
            </YStack>
        </LinearGradientBackgroundCard>
    );
};

export const GoalsSlideBase = ({
    renderCardBlur,
}: {
    renderCardBlur: () => ReactNode;
}) => {
    const { results, terminalAction, onTrackEvent } =
        useConversationalMMContext();
    const { moneyMap, rawChatResults } = results;
    const { goalsTotalCents, spendTotalCents } = moneyMap;

    const isTrialSignup = terminalAction.mode === 'signup';

    const goalPhrase = useMemo(() => {
        const goalsSystemHighInterest =
            rawChatResults.branch === CMMBranch.GoalsSystem &&
            (rawChatResults.debts ?? []).some(
                (d) =>
                    d.annualInterestRatePercent != null &&
                    d.annualInterestRatePercent / 100 > HIGH_INTEREST_THRESHOLD,
            );
        const isSavingsPath =
            rawChatResults.branch === CMMBranch.GoalsSavings ||
            (rawChatResults.branch === CMMBranch.GoalsSystem &&
                !goalsSystemHighInterest) ||
            (rawChatResults.branch === CMMBranch.MoneySystem &&
                rawChatResults.savings != null) ||
            (rawChatResults.branch === CMMBranch.GoalsDebt &&
                (rawChatResults.debts == null ||
                    rawChatResults.debts.length === 0) &&
                rawChatResults.savings != null);
        if (isSavingsPath) {
            const MAX_CENTS = 20_000_000;
            const goals = rawChatResults.savings ?? [];
            const under = goals.filter((g) => g.targetAmountCents <= MAX_CENTS);
            const primary =
                under.length > 0
                    ? under.reduce((best, g) =>
                          g.targetAmountCents > best.targetAmountCents
                              ? g
                              : best,
                      )
                    : goals.length > 0
                      ? goals.reduce((best, g) =>
                            g.targetAmountCents < best.targetAmountCents
                                ? g
                                : best,
                        )
                      : undefined;
            const name = primary?.description;
            return name != null && name !== ''
                ? `save for ${withArticle(name)}`
                : 'reach your goals';
        }
        return 'get out of debt';
    }, [rawChatResults.branch, rawChatResults.savings, rawChatResults.debts]);

    const [containerWidth, setContainerWidth] = useState(0);

    const onLayout = useCallback((event: LayoutChangeEvent) => {
        const { width } = event.nativeEvent.layout;
        setContainerWidth(width - 48);
    }, []);

    const { ruleSetDisplays } = useMemo(() => {
        const simulation = buildSimulation({
            ...rawChatResults,
            monthlySpendCents: spendTotalCents,
            monthlyGoalsSpendCents: goalsTotalCents,
        });

        const displayResult =
            simulation != null && goalsTotalCents > 0
                ? computeRuleSetDisplays(simulation)
                : {
                      ruleSets: buildStaticRuleSetDisplays(rawChatResults),
                      exceedsDateThreshold: true,
                      emergencyFundUnreachable: true,
                      noGoalsMoney: true,
                  };

        const { ruleSets } = displayResult;

        return {
            ruleSetDisplays: ruleSets.length > 0 ? ruleSets : null,
        };
    }, [rawChatResults, spendTotalCents, goalsTotalCents]);

    const [expandedCards, setExpandedCards] = useState(false);
    const expandProgress = useSharedValue(0);

    useEffect(() => {
        expandProgress.value = withTiming(expandedCards ? 1 : 0, {
            duration: EXPAND_DURATION_MS,
            easing: Easing.inOut(Easing.ease),
        });
        if (expandedCards) {
            onTrackEvent(ConversationalMMAnalyticsEvent.GoalsCardsExpanded);
        }
    }, [expandedCards]);

    const cardStackPaddingStyle = useAnimatedStyle(() => ({
        paddingBottom: interpolate(expandProgress.value, [0, 1], [16, 0]),
    }));

    return (
        <YStack
            width="100%"
            justifyContent="center"
            alignItems="center"
            gap={24}
            onLayout={isTrialSignup ? onLayout : undefined}
        >
            <LinearGradientBackgroundCard>
                <YStack
                    gap={24}
                    width="100%"
                    justifyContent="center"
                    alignItems="center"
                >
                    <Header
                        title="Goals"
                        description="This is what your Money System is built to do."
                    />

                    <BodyLargeRegular
                        style={{
                            fontFamily: FontFamilyName.SourceSerifRegular,
                        }}
                    >
                        Whatever&apos;s left after bills and spending flows to
                        your goals automatically, in priority order, so you{' '}
                        {goalPhrase} faster without managing any of it by hand.
                    </BodyLargeRegular>
                    <Animated.View
                        style={[
                            goalCardStyles.cardStack,
                            cardStackPaddingStyle,
                        ]}
                        onPointerEnter={() => {
                            if (isNonMobileWeb) {
                                setExpandedCards(true);
                            }
                        }}
                        onPointerLeave={() => {
                            if (isNonMobileWeb) {
                                setExpandedCards(false);
                            }
                        }}
                        onTouchEnd={() => {
                            if (isNonMobileWeb) return;
                            setExpandedCards((prev) => !prev);
                        }}
                    >
                        {(ruleSetDisplays ?? [])
                            .map((ruleSetDisplay) => ruleSetDisplay.rules)
                            .flat()
                            .map((ruleCompletionInfo, i) => (
                                <AnimatedGoalCard
                                    key={ruleCompletionInfo.description}
                                    index={i}
                                    expandProgress={expandProgress}
                                    expandedCards={expandedCards}
                                >
                                    <XStack
                                        width="100%"
                                        borderRadius={16}
                                        overflow="hidden"
                                        pointerEvents={
                                            expandedCards || i < 3
                                                ? 'auto'
                                                : 'none'
                                        }
                                        {...Shadows.LARGE}
                                        backgroundColor={
                                            isWeb
                                                ? 'rgba(255, 255, 255, 0.50)'
                                                : undefined
                                        }
                                        backdropFilter={
                                            isWeb ? 'blur(20px)' : undefined
                                        }
                                    >
                                        {!isWeb && renderCardBlur()}
                                        <XStack
                                            flex={1}
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={16}
                                            paddingHorizontal={16}
                                            paddingVertical={16}
                                        >
                                            <XStack
                                                alignItems="center"
                                                justifyContent="center"
                                                gap={16}
                                                flexShrink={1}
                                            >
                                                <FruitfulCustomIcon
                                                    icon="fruitful"
                                                    color="red"
                                                    size="sm"
                                                    showShadow={false}
                                                />
                                                <YStack flexShrink={1}>
                                                    <BodySmallRegular
                                                        ellipsizeMode="tail"
                                                        numberOfLines={1}
                                                    >
                                                        {
                                                            ruleCompletionInfo.description
                                                        }
                                                    </BodySmallRegular>
                                                    <BodyMediumEmphasized
                                                        ellipsizeMode="tail"
                                                        numberOfLines={1}
                                                    >
                                                        {
                                                            ruleCompletionInfo.targetDisplay
                                                        }
                                                    </BodyMediumEmphasized>
                                                </YStack>
                                            </XStack>
                                            {ruleCompletionInfo.completionLabel !=
                                                null && (
                                                <BadgeLabel
                                                    variant="neutral"
                                                    text={
                                                        ruleCompletionInfo.completionLabel
                                                    }
                                                />
                                            )}
                                        </XStack>
                                    </XStack>
                                </AnimatedGoalCard>
                            ))}
                    </Animated.View>
                    {terminalAction.mode === 'exit' && (
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            text={terminalAction.label ?? 'Back to homepage'}
                            onPress={terminalAction.onPress}
                        />
                    )}
                </YStack>
            </LinearGradientBackgroundCard>
            {terminalAction.mode === 'signup' && (
                <TrialSignupCard
                    containerWidth={containerWidth}
                    isSubmitting={terminalAction.isLoading ?? false}
                    onSubmit={terminalAction.onPress}
                />
            )}
            {terminalAction.mode === 'signup' && <TrustpilotRatingBadge />}
            <YStack width="100%">
                <LabelRegular color="$black">
                    Certified Financial Planner Board of Standards, Inc. (CFP
                    Board) owns the CFP® certification mark, the CERTIFIED
                    FINANCIAL PLANNER® certification mark, and the CFP®
                    certification mark (with plaque design) logo in the United
                    States, which it authorizes use of by individuals who
                    successfully complete CFP Board&apos;s initial and ongoing
                    certification requirements.
                </LabelRegular>
            </YStack>
        </YStack>
    );
};

const goalCardStyles = StyleSheet.create({
    cardStack: {
        gap: 12,
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
    },
    blurFill: {
        backgroundColor: 'rgba(255, 255, 255, 0.50)',
    },
});

export { goalCardStyles };
